import { useState } from "react";
import { ago, api, can, CONTENT_STATUS, Drawer, Empty, fmtDate, fmtDateTime, go, Loading, Pill, StatusPill, useLoad, useMe } from "./lib";
import { usd } from "./finance";
import { AgreementPill } from "./partners";
import { ReleaseDate, ReleaseFlags } from "./product";
import { AlertTriangle, Inbox, Target, Wallet } from "lucide-react";
import { CountUp } from "./widgets";

const fmtMonth = (ym: string | null) => (ym ? new Date(ym + "-15T12:00:00Z").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "unknown");

const KIND_LABEL: Record<string, string> = { live: "Live", imported: "Imported", manual: "Entered by hand" };

function fmtMetric(m: any) {
  if (m.value === null || m.value === undefined) return null;
  return m.unit === "usd" ? `$${Number(m.value).toLocaleString()}` : Number(m.value).toLocaleString();
}

function MetricTile({ m, onEdit }: { m: any; onEdit?: () => void }) {
  const v = fmtMetric(m);
  return (
    <div className="stat">
      <div className="row between"><span className="label">{m.label}</span>{onEdit && <button className="btn ghost sm" style={{ padding: "0 4px" }} onClick={onEdit}>Update</button>}</div>
      <span className={`value ${v === null ? "unknown" : ""}`}>{v ?? "Unknown"}</span>
      <span className="src">
        {v === null ? m.source : <>{KIND_LABEL[m.kind]} · {m.source}{m.kind === "manual" && m.as_of ? ` · ${ago(m.as_of)}` : ""}</>}
      </span>
      {v !== null && m.caveat && <span className="src" style={{ color: "var(--warn)" }}>{m.caveat}</span>}
    </div>
  );
}

export function Overview() {
  const { me, toast } = useMe();
  const { data, error, reload } = useLoad<any>("/api/overview");
  const [edit, setEdit] = useState<any>(null);
  if (!data) return <div className="page"><Loading error={error} /></div>;
  const goal = data.goals[0];
  const daysLeft = goal ? Math.ceil((new Date(goal.due_on + "T23:59:59-06:00").getTime() - Date.now()) / 86400000) : 0;
  const canEditMetric = (m: any) => can(me, "metrics.edit") && (!m.sensitive || can(me, "finance.edit"));
  const overdue = data.departments.reduce((n: number, d: any) => n + d.overdue, 0);
  const rw = data.runway;
  const pct = goal && goal.current != null ? Math.min(100, (goal.current / goal.target) * 100) : 0;
  return (
    <div className="page">
      <div className="widgets">
        {goal && (
          <div className="widget hero area-daily">
            <span className="w-label"><Target size={14} strokeWidth={2.2} aria-hidden />Goal: {goal.name}</span>
            <span className="w-big">{goal.current === null ? "Unknown" : <CountUp value={Number(goal.current)} />}<span style={{ fontSize: 18, opacity: 0.8 }}> / {Number(goal.target).toLocaleString()}</span></span>
            <div className="progress" style={{ background: "rgba(255,255,255,.3)" }}><div style={{ width: `${pct}%`, background: "#fff" }} /></div>
            <span className="w-sub">{goal.current === null ? "Unknown until every store is connected" : `${Math.round(pct)}%`} · {daysLeft > 0 ? `${daysLeft} days left` : "Due date passed"}</span>
          </div>
        )}
        {rw && (
          <a className="widget area-finance" href="#/finance">
            <span className="w-label"><Wallet size={14} strokeWidth={2.2} aria-hidden />Cash runway</span>
            {rw.status !== "ok" ? <span className="w-sub">Budget not imported yet.</span> : (
              <>
                <span className="w-big" style={{ fontSize: 26 }}>{rw.runs_out ? `Out ${fmtMonth(rw.runs_out)}` : `Past ${fmtMonth(rw.plan_ends)}`}</span>
                <span className="w-sub">{rw.end_cash_this_month != null ? `${usd(rw.end_cash_this_month)} planned at month end · ` : ""}{rw.scenario} plan, not the bank</span>
                {rw.brex_last_month && <span className="w-sub">{usd(rw.brex_last_month.total)} spent through Brex last month</span>}
              </>
            )}
          </a>
        )}
        <a className="widget area-company" href="#/" >
          <span className="w-label"><Inbox size={14} strokeWidth={2.2} aria-hidden />Decisions this week</span>
          <span className="w-big"><CountUp value={data.decisions_week.length + data.decisions.length} /></span>
          <span className="w-sub">{data.decisions_week.length} due by you · {data.decisions.length} content reviews</span>
        </a>
        <a className="widget area-company" href="#/tasks?scope=everyone&due=overdue">
          <span className="w-label"><AlertTriangle size={14} strokeWidth={2.2} aria-hidden />Overdue</span>
          <span className="w-big"><CountUp value={overdue} /></span>
          <span className="w-sub">{data.tasks.blocked.length} blocked · {data.tasks.due_soon.length} tasks due in 7 days</span>
        </a>
      </div>

      <div className="grid">
        <section className="card stack">
          <h2>Top risks</h2>
          {data.top_risks.length ? (
            <div className="list">
              {data.top_risks.map((r: any, i: number) => (
                <a key={i} className="item clickable" href={r.href} style={{ textDecoration: "none", color: "inherit" }}>
                  <div className="grow">
                    <div className="title">{r.title}</div>
                    <div className="small muted">{r.why}</div>
                  </div>
                  <Pill>{r.area}</Pill>
                </a>
              ))}
            </div>
          ) : <Empty>No risks flagged yet. Each department's Claude's take adds them.</Empty>}
          {data.top_risks.length > 0 && <span className="tiny muted">From each department's Claude's take. AI suggestions, check them.</span>}
        </section>
        <section className="card stack">
          <h2>Decisions due this week</h2>
          {data.decisions_week.length || data.decisions.length ? (
            <div className="list">
              {data.decisions_week.map((d: any) => (
                <a key={d.id} className="item clickable" href="#/" style={{ textDecoration: "none", color: "inherit" }}>
                  <div className="grow"><div className="title">{d.title}</div></div>
                  {(d.priority === "urgent" || d.priority === "high") && <Pill tone="bad">{d.priority === "urgent" ? "Urgent" : "High"}</Pill>}
                  <Pill tone={d.due_on < data.today ? "bad" : ""}>Due {fmtDate(d.due_on)}</Pill>
                </a>
              ))}
              {data.decisions.map((a: any) => (
                <div key={a.id} className="item clickable" onClick={() => go("/marketing", { content: a.record_id })}>
                  <div className="grow"><div className="title">{a.title}</div><div className="small muted">Content v{a.version} from {a.requested_by_name} · {ago(a.created_at)}</div></div>
                  <Pill tone="info">Review</Pill>
                </div>
              ))}
            </div>
          ) : <Empty>Nothing due this week.</Empty>}
        </section>
      </div>

      <section className="card stack">
        <h2>Departments</h2>
        <div className="list">
          {data.departments.map((d: any) => (
            <a key={d.area} className="item clickable" href={d.href} style={{ textDecoration: "none", color: "inherit", alignItems: "flex-start" }}>
              <div className="grow">
                <div className="title">{d.label}{d.goal ? <span className="muted small" style={{ fontWeight: 400 }}> · {d.goal}</span> : null}</div>
                <div className="small muted">{d.headline ? <>{d.headline} <span className="tiny">({ago(d.headline_at)})</span></> : "No Claude's take yet. Open the page and ask for one."}</div>
              </div>
              <span className="row" style={{ gap: 6 }}>
                {d.overdue > 0 && <Pill tone="bad">{d.overdue} overdue</Pill>}
                <Pill>{d.this_week} this week</Pill>
              </span>
            </a>
          ))}
        </div>
      </section>

      <div className="row between">
        <h2 className="dept-h">The details</h2>
        <a className="small" href="#/analytics">Usage analytics</a>
      </div>
      <div className="grid-tight">
        {data.metrics.map((m: any) => <MetricTile key={m.key} m={m} onEdit={canEditMetric(m) ? () => setEdit(m) : undefined} />)}
      </div>

      <div className="grid">
        <section className="card">
          <h2>Active campaigns</h2>
          {data.campaigns.length ? (
            <div className="list">
              {data.campaigns.map((c: any) => (
                <div key={c.id} className="item clickable" onClick={() => go("/marketing", { tab: "campaigns", campaign: c.id })}>
                  <div className="grow"><div className="title">{c.name}</div><div className="small muted">{c.owner_name}{c.ends_on ? ` · ends ${fmtDate(c.ends_on)}` : ""}</div></div>
                  {c.is_sample ? <Pill tone="sample">Sample</Pill> : null}
                </div>
              ))}
            </div>
          ) : (
            <Empty>No active campaigns.</Empty>
          )}
        </section>
        <section className="card">
          <h2>Content in the next 14 days</h2>
          {data.upcoming_content.length ? (
            <div className="list">
              {data.upcoming_content.map((c: any) => (
                <div key={c.id} className="item clickable" onClick={() => go("/marketing", { content: c.id })}>
                  <div className="grow"><div className="title">{c.title}</div><div className="small muted">{fmtDateTime(c.scheduled_at)}</div></div>
                  <StatusPill s={c.status} map={CONTENT_STATUS} />
                </div>
              ))}
            </div>
          ) : (
            <Empty>Nothing dated in the next two weeks.</Empty>
          )}
        </section>
      </div>

      <ReleaseFlags flags={data.release_flags} onResolved={reload} />
      {data.releases && (
        <section className="card">
          <h2>Releases and readiness</h2>
          <p className="sub">iOS and Android tracked separately. A target date isn't a live date until someone verifies it.</p>
          {data.releases.length ? (
            <div className="list">
              {data.releases.map((r: any) => (
                <div key={r.id} className="item clickable" onClick={() => go("/product", { release: r.id })}>
                  <div className="grow"><div className="title">{r.name}</div><div className="small muted">Ready {r.readiness.done}/{r.readiness.total}{r.open_flags ? ` · ${r.open_flags} linked items to review` : ""}</div></div>
                  <ReleaseDate r={r} />
                </div>
              ))}
            </div>
          ) : <Empty>No releases tracked yet.</Empty>}
        </section>
      )}

      {(data.partners || data.budget) && (
        <div className="grid">
          {data.budget && (
            <section className="card clickable" onClick={() => go("/finance", data.budget.scenario_id ? { scenario: data.budget.scenario_id } : {})}>
              <h2>Budget vs actual, this month</h2>
              {data.budget.status !== "ok" ? (
                <Empty>The budget hasn't been imported yet.</Empty>
              ) : !data.budget.month ? (
                <Empty>This month isn't in the imported plan.</Empty>
              ) : (
                <>
                  <p className="sub">Plan: {data.budget.scenario} scenario of the Budget Model (imported, reconciled).</p>
                  <div className="grid-tight">
                    <div className="stat"><span className="label">Planned</span><span className="value">{usd(data.budget.month.planned.total)}</span><span className="src">Imported</span></div>
                    <div className="stat"><span className="label">Committed</span><span className={`value ${data.budget.month.committed ? "" : "unknown"}`}>{data.budget.month.committed ? usd(data.budget.month.committed.total) : "None recorded"}</span><span className="src">Entered by hand</span></div>
                    <div className="stat"><span className="label">Actual</span><span className={`value ${data.budget.month.actual ? "" : "unknown"}`}>{data.budget.month.actual ? usd(data.budget.month.actual.total) : "Unknown"}</span><span className="src">Entered by hand · Brex not connected</span></div>
                  </div>
                </>
              )}
            </section>
          )}
          {data.partners && (
            <section className="card">
              <h2>Partnership commitments</h2>
              <p className="sub">{data.partners.orgs.filter((o: any) => o.commitment_confirmed).length} confirmed · {data.partners.orgs.filter((o: any) => !o.commitment_confirmed).length} not signed yet</p>
              <div className="list">
                {data.partners.orgs.map((o: any) => (
                  <div key={o.id} className="item clickable" onClick={() => go("/partnerships", { org: o.id })}>
                    <div className="grow"><div className="title">{o.name}</div><div className="small muted">{o.customer_offer || o.relationship}</div></div>
                    <AgreementPill o={o} />
                  </div>
                ))}
              </div>
              {data.partners.deliverables.length > 0 && (
                <>
                  <strong className="small" style={{ display: "block", marginTop: 12 }}>Open deliverables</strong>
                  {data.partners.deliverables.map((d: any) => (
                    <div key={d.id} className="small row between" style={{ padding: "6px 0" }}>
                      <span>{d.org_name}: {d.title}</span>
                      <Pill tone={d.due_on && d.date_confirmed ? "" : "warn"}>{d.due_on ? fmtDate(d.due_on) : "No date yet"}</Pill>
                    </div>
                  ))}
                </>
              )}
            </section>
          )}
        </div>
      )}

      {edit && (
        <Drawer title={`Update: ${edit.label}`} onClose={() => setEdit(null)}>
          <p className="small muted" style={{ margin: 0 }}>{edit.definition}</p>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              try {
                await api(`/api/metrics/${edit.key}`, { method: "PATCH", body: { value: fd.get("value"), source: fd.get("source") } });
                toast("Saved, marked as entered by hand");
                setEdit(null);
                reload();
              } catch (err: any) {
                toast(err.message, true);
              }
            }}
          >
            <label className="field">Value (leave empty if unknown)<input className="input" name="value" type="number" step="any" defaultValue={edit.value ?? ""} /></label>
            <label className="field">Where this number came from<input className="input" name="source" required placeholder="e.g. App Store Connect, Sales and Trends, Nov 3" /></label>
            <div className="row"><button className="btn primary">Save</button></div>
          </form>
        </Drawer>
      )}
    </div>
  );
}

const STATUS: Record<string, { label: string; tone: string }> = {
  ok: { label: "Connected", tone: "good" },
  error: { label: "Failing", tone: "bad" },
  not_configured: { label: "Not set up", tone: "" },
};

export function Connections({ list, onSynced }: { list: any[]; onSynced: () => void }) {
  const { me, toast } = useMe();
  const [busy, setBusy] = useState(false);
  if (!list?.length) return null;
  return (
    <section className="card stack">
      <div className="row between">
        <div>
          <h2>Subscriber sources</h2>
          <p className="sub" style={{ margin: 0 }}>Checked every 6 hours. Store reports run a day (Apple) to a week (Google) behind.</p>
        </div>
        {can(me, "metrics.edit") && (
          <button
            className="btn sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api("/api/integrations/sync", { method: "POST" });
                toast("Checked both stores");
                onSynced();
              } catch (e: any) {
                toast(e.message, true);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Checking…" : "Sync now"}
          </button>
        )}
      </div>
      {list.map((i) => (
        <div key={i.id} className="stack" style={{ gap: 4 }}>
          <div className="row between">
            <strong className="small">{i.name}</strong>
            <span className="row" style={{ gap: 8 }}>
              <span className="muted tiny">{i.last_success_at ? `Last good sync ${ago(i.last_success_at)}` : "Never synced"}</span>
              <Pill tone={STATUS[i.status]?.tone}>{STATUS[i.status]?.label ?? i.status}</Pill>
            </span>
          </div>
          {i.status === "error" && <div className="banner bad small">{i.last_error}{i.last_success_at ? " The numbers below are from the last good sync." : ""}</div>}
          {i.status === "not_configured" && <span className="muted tiny">Needs keys in the portal's .env file. See docs/SUBSCRIBER-SETUP.md.</span>}
        </div>
      ))}
    </section>
  );
}

const WHAT: Record<string, string> = {
  app_store_connect: "iOS paid subscribers and trials. Every 6 hours.",
  google_play: "Android subscriptions. Every 6 hours.",
  github: "Issues, pull requests and milestones for Product. Every 30 minutes.",
  figma: "Design files and comments for Marketing and Product. Hourly.",
  bigquery: "Usage analytics summaries. Every 6 hours.",
};

// Admin: every outside connection the portal reads from, with its last good sync. Moved off Overview, since only the admin acts on it.
export function ConnectionsPage() {
  const { me, toast } = useMe();
  const { data, error, reload } = useLoad<any[]>("/api/integrations");
  const ai = useLoad<any>(can(me, "decisions.decide") ? "/api/settings/ai" : null).data;
  const [busy, setBusy] = useState(false);
  if (!data) return <div className="page"><Loading error={error} /></div>;
  return (
    <div className="page">
      <section className="card stack">
        <div className="row between">
          <div>
            <h2>Data connections</h2>
            <p className="sub" style={{ margin: 0 }}>Read-only. The portal never writes back to these services.</p>
          </div>
          {can(me, "metrics.edit") && (
            <button
              className="btn sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api("/api/integrations/sync", { method: "POST" });
                  toast("Checked both stores");
                  reload();
                } catch (e: any) {
                  toast(e.message, true);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Checking…" : "Sync the stores now"}
            </button>
          )}
        </div>
        <div className="list">
          {data.map((i) => (
            <div key={i.id} className="item" style={{ alignItems: "flex-start" }}>
              <div className="grow stack" style={{ gap: 2 }}>
                <div className="title">{i.name}</div>
                <div className="small muted">{WHAT[i.id] ?? ""}</div>
                {i.status === "error" && i.last_error && <div className="banner bad small" style={{ marginTop: 4 }}>{i.last_error}</div>}
              </div>
              <span className="stack" style={{ gap: 4, alignItems: "flex-end" }}>
                <Pill tone={STATUS[i.status]?.tone}>{STATUS[i.status]?.label ?? i.status}</Pill>
                <span className="muted tiny">{i.last_success_at ? `Last good sync ${ago(i.last_success_at)}` : "Never synced"}</span>
              </span>
            </div>
          ))}
          {ai && (
            <div className="item">
              <div className="grow"><div className="title">Claude (Anthropic)</div><div className="small muted">Claude's takes, recommendations and brain-dump sorting. Model {ai.model}.</div></div>
              <Pill tone={ai.connected ? "good" : ""}>{ai.connected ? "Connected" : "Not set up"}</Pill>
            </div>
          )}
          <div className="item">
            <div className="grow"><div className="title">Text alerts (AWS)</div><div className="small muted">Texts that say only how many things need someone.</div></div>
            <Pill tone={me.texts_ready ? "good" : ""}>{me.texts_ready ? "Connected" : "Waiting on carrier approval"}</Pill>
          </div>
        </div>
        <p className="small muted" style={{ margin: 0 }}>Google Calendar and Chat connect per person, in Settings.</p>
      </section>
    </div>
  );
}
